import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Search, Plus, Edit, Trash2, X, Eye, Filter, FileText, FileSpreadsheet,
  RefreshCcw, LayoutGrid, Shield, Layers, Clock, CheckCircle, XCircle, Users, Download,
  Upload, ChevronDown, ChevronUp, FileOutput, User, CalendarCheck, List, LayoutList,
} from 'lucide-react';
import { MessageSquare } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { useHR } from '../../core/contexts/HRContext';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker } from '../../shared/components/custom';
import { exportToCSV, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeFromLabel,
  useHrEmployees,
  sliceHrListItems,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

/** Saudi Labor Law leave type keys (no French RTT / congés spécifiques) */
const SAUDI_LEAVE_TYPES = [
  'conge_annuel',
  'conge_maladie',
  'conge_maternite',
  'conge_hajj',
  'conge_deuil',
  'conge_mariage',
  'conge_etude',
  'conge_sans_solde',
  'conge_paternite',
];

const HR_TABLE_COLUMNS = [
  { key: 'employee', labelKey: 'col_employee' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'startDate', labelKey: 'col_start_date' },
  { key: 'endDate', labelKey: 'col_end_date' },
  { key: 'days', labelKey: 'col_days' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const LeavesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.leavesPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const hrConfig = useHrConfig();

  const formatLeaveDate = (value) => {
    if (value == null || value === '') return p('dash');
    const d = new Date(value);
    return !Number.isNaN(d.getTime()) ? d.toLocaleDateString(dateLocale) : p('dash');
  };

  const { 
    leaves,
    addLeave, 
    updateLeave, 
    removeLeave, 
    refreshData
  } = useHR();

  const { employees: realEmployees } = useHrEmployees();

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [viewingLeave, setViewingLeave] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [mainViewMode, setMainViewMode] = useState('all_leaves');
  const [quickFilter, setQuickFilter] = useState('all');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  
  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    type: '',
    startDate: '',
    endDate: '',
    days: 0,
    status: 'en_attente',
    reason: '',
    comments: ''
  });

  const triggerRefresh = () => refreshData();

  const stats = {
    total: leaves.length,
    en_attente: leaves.filter(l => l.status === 'en_attente').length,
    approuve: leaves.filter(l => l.status === 'approuve').length,
    refuse: leaves.filter(l => l.status === 'refuse').length,
    totalDays: leaves.reduce((sum, l) => sum + l.days, 0)
  };

  const leaveDistribution = useMemo(() => ({
    all_leaves: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'en_attente', name: p('dist_pending'), count: stats.en_attente, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'approuve', name: p('dist_approved'), count: stats.approuve, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'refuse', name: p('dist_rejected'), count: stats.refuse, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'en_attente', name: p('dist_pending'), count: stats.en_attente, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'approuve', name: p('dist_approved'), count: stats.approuve, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'refuse', name: p('dist_rejected'), count: stats.refuse, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [...new Set(leaves.map((l) => l.type))].map((type) => ({
        filterKey: type,
        name: (() => {
          const labels = {
            conge_annuel: p('type_annual'),
            conge_maladie: p('type_sick'),
            conge_maternite: p('type_maternity'),
            conge_hajj: p('type_hajj'),
            conge_deuil: p('type_bereavement'),
            conge_mariage: p('type_marriage'),
            conge_etude: p('type_study'),
            conge_sans_solde: p('type_unpaid'),
            conge_paternite: p('type_paternity'),
          };
          return labels[type] || p('type_other');
        })(),
        count: leaves.filter((l) => l.type === type).length,
        color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      })),
    },
  }), [t, leaves, stats.en_attente, stats.approuve, stats.refuse]);

  const distributionInfo = leaveDistribution[mainViewMode] || leaveDistribution.all_leaves;

  const mainTabs = useMemo(() => ([
    { id: 'all_leaves', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Shield },
    { id: 'par_type', name: p('mainTab_by_type'), icon: Layers },
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Calendar },
    { id: 'en_attente', name: p('quickFilter_pending'), icon: Clock },
    { id: 'approuve', name: p('quickFilter_approved'), icon: CheckCircle },
    { id: 'refuse', name: p('quickFilter_rejected'), icon: XCircle },
  ]), [t]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseFilteredLeaves = useMemo(() => leaves.filter((leave) => {
    const matchesSearch = !searchTerm || leave.employeeName?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (quickFilter !== 'all' && leave.status !== quickFilter) return false;
    return true;
  }), [leaves, searchTerm, quickFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employeeName?.trim() || !formData.type || !formData.startDate || !formData.endDate) {
      notifyError(showNotify, p('save_error'));
      return;
    }
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      notifyError(showNotify, p('save_error'));
      return;
    }
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const updatedData = { ...formData, days };
    
    try {
      if (editingLeave) {
        await updateLeave(editingLeave.id, updatedData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الإجازة' }));
      } else {
        await addLeave(updatedData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الإجازة' }));
      }
      resetForm();
    } catch (error) {
      console.error('Error saving leave:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const resetForm = () => {
    setFormData({ employeeId: '', employeeName: '', type: '', startDate: '', endDate: '', days: 0, status: 'en_attente', reason: '', comments: '' });
    setEditingLeave(null);
    setShowModal(false);
  };

  const openNewLeaveModal = () => {
    setEditingLeave(null);
    setFormData({ employeeId: '', employeeName: '', type: '', startDate: '', endDate: '', days: 0, status: 'en_attente', reason: '', comments: '' });
    setShowModal(true);
  };

  const handleBulkDelete = async () => { 
    if (window.confirm(p('bulk_delete_confirm', { count: selectedLeaves.length }))) { 
      try {
        const deleteCount = selectedLeaves.length;
        await Promise.all(selectedLeaves.map(id => removeLeave(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'إجازة' }));
        setSelectedLeaves([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('delete_error'));
      }
    }
  };
  
  const handleEdit = (leave) => {
    setEditingLeave(leave);
    setFormData({
      employeeId: leave.employeeId?.toString() || '',
      employeeName: leave.employeeName || '',
      type: leave.type || '',
      startDate: leave.startDate || '',
      endDate: leave.endDate || '',
      days: leave.days ?? 0,
      status: leave.status || 'en_attente',
      reason: leave.reason || '',
      comments: leave.comments || '',
    });
    setShowModal(true);
  };
  
  const handleDelete = async (id) => { 
    if (window.confirm(p('delete_confirm'))) { 
      try {
        await removeLeave(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الإجازة' }));
      } catch (error) {
        console.error('Error deleting leave:', error);
        notifyError(showNotify, p('delete_error'));
      }
    }
  };
  
  const handleView = (leave) => { setViewingLeave(leave); setShowViewModal(true); };

  const getStatusLabel = (status) => {
    const labels = {
      en_attente: p('status_pending'),
      approuve: p('status_approved'),
      refuse: p('status_rejected'),
    };
    return labels[status] || p('dash');
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      conge_annuel: p('type_annual_full'),
      conge_maladie: p('type_sick_full'),
      conge_maternite: p('type_maternity'),
      conge_hajj: p('type_hajj'),
      conge_deuil: p('type_bereavement'),
      conge_mariage: p('type_marriage'),
      conge_etude: p('type_study'),
      conge_sans_solde: p('type_unpaid_full'),
      conge_paternite: p('type_paternity'),
      absence: p('type_absence'),
    };
    return labels[type] || type || p('dash');
  };

  const columnFilterRules = useMemo(() => [
    textColumnRule('employee', (r) => r.employeeName || ''),
    textColumnRule('type', (r) => getLeaveTypeLabel(r.type)),
    textColumnRule('startDate', (r) => (r.startDate ? formatLeaveDate(r.startDate) : '')),
    textColumnRule('endDate', (r) => (r.endDate ? formatLeaveDate(r.endDate) : '')),
    textColumnRule('days', (r) => String(r.days ?? '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], [p]);

  const filteredLeaves = useMemo(
    () => sliceHrListItems(
      applyColumnFilters(baseFilteredLeaves, colFilters, columnFilterRules),
      hrConfig.itemsPerPage,
    ),
    [baseFilteredLeaves, colFilters, columnFilterRules, hrConfig.itemsPerPage],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredLeaves, selectedLeaves, setSelectedLeaves);

  const leaveTypeOptions = useMemo(() => ([
    { value: 'conge_annuel', label: p('type_annual_full') },
    { value: 'conge_maladie', label: p('type_sick_full') },
    { value: 'conge_maternite', label: p('type_maternity') },
    { value: 'conge_hajj', label: p('type_hajj') },
    { value: 'conge_deuil', label: p('type_bereavement') },
    { value: 'conge_mariage', label: p('type_marriage') },
    { value: 'conge_etude', label: p('type_study') },
    { value: 'conge_sans_solde', label: p('type_unpaid_full') },
    { value: 'conge_paternite', label: p('type_paternity') },
  ]), [t]);

  const leaveStatusOptions = useMemo(() => ([
    { value: 'en_attente', label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'approuve', label: p('status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'refuse', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [t]);

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: p('quickFilter_all') },
      ...leaveStatusOptions.map((s) => ({ value: s.value, label: s.label })),
    ],
    [leaveStatusOptions, p],
  );

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Calendar, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.en_attente, icon: Clock, variant: 'forest' },
      { key: 'approved', label: p('stat_approved'), value: stats.approuve, icon: CheckCircle, variant: 'copper' },
      { key: 'rejected', label: p('stat_rejected'), value: stats.refuse, icon: XCircle, variant: 'forest' },
    ],
    [stats, p],
  );

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const leaveStatusFilterOptions = useMemo(
    () => leaveStatusOptions.map((s) => ({ value: s.value, label: s.label })),
    [leaveStatusOptions],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_leaves' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} onClick={() => { setQuickFilter(filter.id); }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
          <div key={item.filterKey || item.name} className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white">{item.name}</h4>
              <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">{(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}{p('percent_of_requests')}</p>
          </div>
        ))}
        </div>
      </motion.div>
    </div>
  );

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredLeaves.map(l => ({
        [p('col_employee')]: l.employeeName,
        [p('col_type')]: getLeaveTypeLabel(l.type),
        [p('col_start_date')]: new Date(l.startDate).toLocaleDateString(dateLocale),
        [p('col_end_date')]: new Date(l.endDate).toLocaleDateString(dateLocale),
        [p('col_days')]: l.days,
        [p('col_status')]: getStatusLabel(l.status)
      }));
      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredLeaves.map(l => ({
        [p('col_employee')]: l.employeeName,
        [p('col_type')]: getLeaveTypeLabel(l.type),
        [p('col_start_date')]: new Date(l.startDate).toLocaleDateString(dateLocale),
        [p('col_end_date')]: new Date(l.endDate).toLocaleDateString(dateLocale),
        [p('col_days')]: l.days,
        [p('col_status')]: getStatusLabel(l.status)
      }));
      await generateReportExcel(p('report_title'), reportData);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleExport = () => {
    const headers = [
      { key: 'employeeName', label: p('col_employee') },
      { key: 'typeLabel', label: p('col_type') },
      { key: 'startDate', label: p('label_start_date') },
      { key: 'endDate', label: p('label_end_date') },
      { key: 'days', label: p('col_days') },
      { key: 'statusLabel', label: p('col_status') },
      { key: 'reason', label: p('label_reason') },
      { key: 'comments', label: p('label_comments') }
    ];
    const dataToExport = filteredLeaves.map((l) => ({
      ...l,
      typeLabel: getLeaveTypeLabel(l.type),
      statusLabel: getStatusLabel(l.status),
      startDate: l.startDate ? new Date(l.startDate).toLocaleDateString(dateLocale) : '',
      endDate: l.endDate ? new Date(l.endDate).toLocaleDateString(dateLocale) : ''
    }));
    const fileName = `conges_export_${new Date().toISOString().split('T')[0]}.csv`;
    try {
      exportToCSV(dataToExport, headers, fileName);
    } catch (error) {
      console.error('[LeavesPage] CSV export error:', error);
      alert(p('export_error'));
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'employeeName', label: p('col_employee') },
      { key: 'type', label: p('col_type') },
      { key: 'startDate', label: p('label_start_date') },
      { key: 'endDate', label: p('label_end_date') },
      { key: 'status', label: p('col_status') },
      { key: 'reason', label: p('label_reason') },
      { key: 'comments', label: p('label_comments') }
    ];
    const schema = {
      requiredFields: ['employeeName'],
      validators: {
        status: (value) => !value || ['en_attente', 'approuve', 'refuse'].includes(String(value).toLowerCase())
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success && result.data?.length) {
          let successCount = 0;
          for (const row of result.data) {
            try {
              const st = row.status ? String(row.status).toLowerCase() : 'en_attente';
              const status = ['en_attente', 'approuve', 'refuse'].includes(st) ? st : 'en_attente';
              const typeRaw = row.type ? String(row.type).toLowerCase().replace(/\s+/g, '_') : 'conge_annuel';
              const type = SAUDI_LEAVE_TYPES.includes(typeRaw) ? typeRaw : 'conge_annuel';
              const start = row.startDate || new Date().toISOString().split('T')[0];
              const end = row.endDate || start;
              const startD = new Date(start);
              const endD = new Date(end);
              const days = Math.max(1, Math.ceil((endD - startD) / (1000 * 60 * 60 * 24)) + 1);
              await addLeave({
                employeeName: row.employeeName,
                type,
                startDate: start,
                endDate: end,
                days,
                status,
                reason: row.reason || '',
                comments: row.comments || ''
              });
              successCount++;
            } catch (err) {
              console.error('[LeavesPage] import row error:', err);
            }
          }
          await refreshData();
          alert(p('import_success', { count: successCount }) + (result.invalidCount ? ` (${result.invalidCount})` : ''));
        } else {
          alert(result.errors?.length ? result.errors.slice(0, 8).join('\n') : p('import_no_data'));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    });
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/leaves/${recordId}`, { status: newStatus });      await refreshData();
      setViewingLeave((prev) =>
        prev && prev.id === recordId ? { ...prev, status: newStatus } : prev
      );
    } catch (error) {
      console.error('Error updating leave status:', error);
    }
  };

  const importExportProps = useMemo(
    () => buildHrImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );


  return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Calendar}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedLeaves.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedLeaves.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewLeaveModal}
        createLabel={p('new_request')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={triggerRefresh} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} />
                    </div>
                  </th>
                  {isColVisible('employee') && (
                    <TableColumnHeaderFilter columnKey="employee" label={p('col_employee')} value={colFilters.employee ?? ''} onChange={(v) => setColFilter('employee', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('type') && (
                    <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={colFilters.type ?? ''} onChange={(v) => setColFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('startDate') && (
                    <TableColumnHeaderFilter columnKey="startDate" label={p('col_start_date')} value={colFilters.startDate ?? ''} onChange={(v) => setColFilter('startDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('endDate') && (
                    <TableColumnHeaderFilter columnKey="endDate" label={p('col_end_date')} value={colFilters.endDate ?? ''} onChange={(v) => setColFilter('endDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('days') && (
                    <TableColumnHeaderFilter columnKey="days" label={p('col_days')} value={colFilters.days ?? ''} onChange={(v) => setColFilter('days', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={leaveStatusFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredLeaves.length > 0 ? (
                  filteredLeaves.map((leave, index) => (
                    <tr key={leave.id} className={`hover:bg-white/5 transition-colors ${selectedLeaves.includes(leave.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox checked={selectedLeaves.includes(leave.id)} onChange={() => handleSelectRecord(leave.id)} />
                        </div>
                      </td>
                      {isColVisible('employee') && <td className="px-6 py-4 text-white font-semibold">{leave.employeeName || '—'}</td>}
                      {isColVisible('type') && <td className="px-6 py-4 text-white/80">{getLeaveTypeLabel(leave.type)}</td>}
                      {isColVisible('startDate') && <td className="px-6 py-4 text-white/80">{formatLeaveDate(leave.startDate)}</td>}
                      {isColVisible('endDate') && <td className="px-6 py-4 text-white/80">{formatLeaveDate(leave.endDate)}</td>}
                      {isColVisible('days') && <td className="px-6 py-4 text-white/80">{leave.days ?? '—'}</td>}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center align-middle">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={leave.status} recordId={leave.id} options={leaveStatusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <QawaemErpRowActions onView={() => handleView(leave)} onEdit={() => handleEdit(leave)} onDelete={() => handleDelete(leave.id)} viewTitle={p('action_view')} editTitle={p('action_edit')} deleteTitle={p('action_delete')} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Calendar size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredLeaves.length > 0 ? (
              filteredLeaves.map((leave, index) => (
                <motion.div
                  key={leave.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <User size={20} className="text-blue-400 shrink-0" />
                          <span className="truncate">{leave.employeeName || '—'}</span>
                        </h3>
                        <QawaemClickableStatusBadge status={leave.status} recordId={leave.id} options={leaveStatusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {getLeaveTypeLabel(leave.type)}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-violet-400 shrink-0" />
                              <span>
                                {formatLeaveDate(leave.startDate)}
                                {leave.endDate && leave.startDate !== leave.endDate
                                  ? ` → ${formatLeaveDate(leave.endDate)}`
                                  : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-white/50 shrink-0" />
                              <span>{leave.days != null ? p('days_count', { count: leave.days }) : p('dash')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <h5 className="font-medium text-white mb-2">{p('card_reason_comments')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {leave.reason ? (
                              <p className="text-white/90">{leave.reason}</p>
                            ) : (
                              <div className="text-white/50">{p('no_reason')}</div>
                            )}
                            {leave.comments ? (
                              <p className="text-white/60 text-xs mt-2 line-clamp-3">{leave.comments}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      onView={() => handleView(leave)}
                      onEdit={() => handleEdit(leave)}
                      onDelete={() => handleDelete(leave.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Calendar size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingLeave ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="leave-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingLeave ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="leave-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_employee_required')} />}</label>
                    <CustomSelect
                      value={formData.employeeName}
                      onChange={(value) => {
                        const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                        if (selectedEmployee) {
                          setFormData({ ...formData, ...buildHrEmployeePickerPatch(selectedEmployee, 'employeeName', 'employeeId') });
                        } else {
                          setFormData({ ...formData, employeeName: value, employeeId: '' });
                        }
                      }}
                      options={buildHrEmployeeSelectOptions(realEmployees, { value: '', label: p('select_employee') })}
                      placeholder={p('select_employee')}
                      searchable={true}
                      creatable={true}
                    />
                    <p className="text-white/50 text-xs mt-1">{p('select_hint')}</p>
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_leave_type_required')} />}</label>
                    <CustomSelect
                      value={formData.type}
                      onChange={(value) => setFormData({ ...formData, type: value })}
                      options={leaveTypeOptions}
                      placeholder={p('select_type')}
                      searchable={true}
                      creatable={true}
                    />
                    <p className="text-white/50 text-xs mt-1">{p('select_type_hint')}</p>
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_start_required')} />}</label>
                    <CustomDatePicker
                      value={formData.startDate}
                      onChange={(value) => setFormData({ ...formData, startDate: value })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_end_required')} />}</label>
                    <CustomDatePicker
                      value={formData.endDate}
                      onChange={(value) => setFormData({ ...formData, endDate: value })}
                      placeholder={p('date_placeholder')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={leaveStatusOptions.map(({ value, label }) => ({ value, label }))}
                      placeholder={p('select_status')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reason')}</label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                      rows={3}
                      placeholder={p('placeholder_reason')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_comments')}</label>
                    <textarea
                      value={formData.comments}
                      onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                      rows={3}
                      placeholder={p('placeholder_comments')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_days', { defaultValue: 'الأيام' })}</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.days}
                      onChange={(e) => setFormData({ ...formData, days: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="0"
                    />
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && viewingLeave && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingLeave(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button
                  onClick={() => { setShowViewModal(false); setViewingLeave(null); }}
                  className="text-white/70 hover:text-white transition-colors"
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
                    {p('view_section_employee_request')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingLeave.employeeName || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_leave_type')}</p>
                      <p className="text-white font-medium">{getLeaveTypeLabel(viewingLeave.type)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex flex-wrap">
                        <QawaemClickableStatusBadge status={viewingLeave.status} recordId={viewingLeave.id} options={leaveStatusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <CalendarCheck size={18} className="text-green-400" />
                    {p('view_section_period')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_start_date')}</p>
                      <p className="text-white font-medium">
                        {viewingLeave.startDate ? new Date(viewingLeave.startDate).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_end_date')}</p>
                      <p className="text-white font-medium">
                        {viewingLeave.endDate ? new Date(viewingLeave.endDate).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_day_count')}</p>
                      <p className="text-white font-medium text-lg">{viewingLeave.days != null ? viewingLeave.days : p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_reference')}</p>
                      <p className="text-white font-medium">{viewingLeave.id != null ? String(viewingLeave.id) : p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-amber-400" />
                      {p('view_section_reason')}
                    </h3>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_description')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">{viewingLeave.reason?.trim() || p('not_provided')}</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <MessageSquare size={18} className="text-purple-400" />
                      {p('view_section_comments')}
                    </h3>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_internal_notes')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">{viewingLeave.comments?.trim() || p('no_comments')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default LeavesPage;


